import { useState } from "react";
import CardScanner from "./CardScanner";

const GAME_TYPES = ["Pokemon", "Lorcana"];

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

export default function QuickScanFlow({
  onClose, onAddInventoryItem, onAddShowItem, vendorId,
  showId = null, showName = null, maxQueue = 40, S, T, toast, confirmAction,
  defaultBuyPercentage = 70,
}) {
  const [scanning, setScanning] = useState(true);
  const [phase, setPhase] = useState("scanning"); // scanning | summary
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const [buyPct, setBuyPct] = useState(defaultBuyPercentage);

  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    CardName: "", GameType: "Pokemon", MarketValue: "", ListPrice: "",
  });

  const newKey = () => `k_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  const fromScanResult = (r) => ({
    CardName: r.cardName, Set: r.setName || "", Number: r.cardNumber || "",
    Rarity: r.rarity || "Rare", Condition: "Near Mint", Language: "English",
    GameType: "Pokemon", ImageURL: r.imageUrl || "",
    MarketValue: r.price || 0, ListPrice: "", BuyPercentage: buyPct,
    _key: newKey(),
  });

  const acquisitionCostFor = (card) => {
    const mv = parseFloat(card.MarketValue) || 0;
    const pct = card.BuyPercentage !== "" && card.BuyPercentage != null
      ? parseFloat(card.BuyPercentage) : buyPct;
    return mv > 0 ? Math.round(mv * ((pct || 0) / 100)) : 0;
  };

  const buildPayload = (card) => ({
    Type: "Single Card", CardName: card.CardName, Set: card.Set,
    Number: card.Number, Rarity: card.Rarity, Language: card.Language,
    Condition: card.Condition, GameType: card.GameType || "Pokemon",
    Quantity: 1, Source: "Individual Buy",
    AcquisitionCost: acquisitionCostFor(card),
    MarketValueAtAcquisition: parseFloat(card.MarketValue) || 0,
    ListPrice: card.ListPrice || "", MinSellPrice: null,
    ImageURL: card.ImageURL || "",
    LotID: "", LotName: "", LotItemNumber: "",
    Status: showId ? "Reserved" : "In Stock",
    ShowID: showId || "", ShowName: showName || "",
  });

  const commitCards = async (cards) => {
    for (const card of cards) {
      const saved = await onAddInventoryItem(buildPayload(card));
      if (showId && onAddShowItem && saved?.ID) {
        await onAddShowItem(saved.ID);
      }
    }
  };

  const addToQueue = (card) => {
    const next = [...queue, card];
    if (next.length >= maxQueue) {
      setQueue(next);
      setScanning(false);
      setPhase("summary");
      toast(`Queue full (${maxQueue} cards) — review before scanning more.`, "alert");
    } else {
      setQueue(next);
    }
  };

  const updateQueueCard = (key, patch) => {
    setQueue(q => q.map(c => c._key === key ? { ...c, ...patch } : c));
  };

  const removeFromQueue = (key) => {
    setQueue(q => q.filter(c => c._key !== key));
    if (editingKey === key) setEditingKey(null);
  };

  const handleManualAdd = () => {
    if (!manualForm.CardName.trim()) { toast("Card name is required", "alert"); return; }
    addToQueue({
      CardName: manualForm.CardName, Set: "", Number: "", Rarity: "Rare",
      Condition: "Near Mint", Language: "English", GameType: manualForm.GameType,
      MarketValue: manualForm.MarketValue || 0, ListPrice: manualForm.ListPrice || "",
      BuyPercentage: buyPct,
      _key: newKey(),
    });
    setManualForm({ CardName: "", GameType: "Pokemon", MarketValue: "", ListPrice: "" });
    setShowManual(false);
  };

  const finishAndAdd = async () => {
    setBusy(true);
    try {
      await commitCards(queue);
      const finalCount = queue.length;
      toast(`${finalCount} card${finalCount !== 1 ? "s" : ""} added to inventory.`, "success");
      onClose(true);
    } catch (e) {
      toast("Failed to finish: " + e.message, "error");
    }
    setBusy(false);
  };

  const handleExit = async () => {
    if (queue.length > 0) {
      const ok = await confirmAction(
        `You have ${queue.length} scanned card${queue.length !== 1 ? "s" : ""} not yet added to inventory. Leaving now will discard them.`,
        { confirmLabel: "Discard & Leave", cancelLabel: "Keep Reviewing", danger: true }
      );
      if (!ok) return;
    }
    onClose(false);
  };

  if (phase === "scanning" && scanning) {
    return (
      <CardScanner
        S={S} T={T} batchMode={true}
        queueCount={queue.length} queueItems={queue} maxQueue={maxQueue}
        onSelect={(r) => addToQueue(fromScanResult(r))}
        onDone={() => { setScanning(false); setPhase("summary"); }}
        onClose={handleExit}
      />
    );
  }

  const smallLabel = { fontSize: 12, fontWeight: 600, color: T?.textFaint || "#888", marginBottom: 4, display: "block" };
  const smallInput = {
    width: "100%", fontSize: 15, padding: "10px 12px", borderRadius: 8,
    background: T?.surface || "#0d1117", border: `1px solid ${T?.border || "rgba(255,255,255,0.2)"}`,
    color: T?.text || "#fff", boxSizing: "border-box",
  };

  return (
    <div style={{position:"fixed", inset:0, zIndex:10000, background:T?.surface || "#0d1117",
      display:"flex", flexDirection:"column", fontFamily:"'Sora',sans-serif"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
        padding:"16px 18px", borderBottom:`1px solid ${T?.border || "rgba(255,255,255,0.1)"}`, flexShrink:0}}>
        <div style={{fontSize:19, fontWeight:700, color:T?.text || "#fff"}}>
          {queue.length} card{queue.length!==1?"s":""} to review
        </div>
        <button onClick={handleExit} style={{background:"none", border:"none",
          color:T?.textMuted || "#888", fontSize:26, cursor:"pointer"}}>×</button>
      </div>

      <div style={{padding:"14px 18px", flexShrink:0, borderBottom:`1px solid ${T?.border || "rgba(255,255,255,0.1)"}`}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12}}>
          <div style={{fontSize:15, fontWeight:600, color:T?.text || "#fff"}}>
            Buy Percentage (default)
          </div>
          <div style={{display:"flex", alignItems:"center", gap:6}}>
            <input type="number" min="0" max="100" value={buyPct}
              onChange={e=>setBuyPct(e.target.value === "" ? "" : parseFloat(e.target.value))}
              style={{width:64, fontSize:16, padding:"8px 10px", borderRadius:8, textAlign:"right",
                background:T?.surface2||"#1a1f2e", border:`1px solid ${T?.border||"rgba(255,255,255,0.2)"}`,
                color:T?.text||"#fff"}} />
            <span style={{color:T?.textMuted||"#888", fontSize:15}}>%</span>
          </div>
        </div>
        <div style={{fontSize:12, color:T?.textFaint||"#888", marginTop:6}}>
          Applied to newly scanned cards. Tap a card below to adjust its price or percentage.
        </div>
      </div>

      <div style={{flex:1, overflowY:"auto", padding:"14px 18px"}}>
        {queue.length === 0 ? (
          <div style={{textAlign:"center", padding:40, color:T?.textFaint||"#666", fontSize:16}}>
            No cards in this batch yet.
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:10}}>
            {queue.map(c => {
              const isEditing = editingKey === c._key;
              const pct = c.BuyPercentage !== "" && c.BuyPercentage != null ? c.BuyPercentage : buyPct;
              const acPreview = acquisitionCostFor(c);
              return (
                <div key={c._key} style={{background:T?.surface2||"#1a1f2e", borderRadius:12, overflow:"hidden"}}>
                  <div onClick={()=>setEditingKey(isEditing ? null : c._key)}
                    style={{display:"flex", alignItems:"center", gap:12, padding:10, cursor:"pointer"}}>
                    <div style={{width:44, height:60, borderRadius:6, overflow:"hidden", flexShrink:0,
                      background:T?.surface||"#0d1117", display:"flex", alignItems:"center", justifyContent:"center"}}>
                      {c.ImageURL ? <img src={c.ImageURL} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        : <span style={{fontSize:20}}>🃏</span>}
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:16, fontWeight:600, color:T?.text||"#fff"}}>{c.CardName}</div>
                      <div style={{fontSize:13, color:T?.textFaint||"#888"}}>
                        {c.ListPrice ? `List ${fmt(c.ListPrice)}` : "No list price"}
                        {" · "}
                        {parseFloat(c.MarketValue) > 0 ? `Market ${fmt(c.MarketValue)}` : "No market price"}
                        {" · "}{pct}% buy
                      </div>
                    </div>
                    <button onClick={(e)=>{e.stopPropagation(); removeFromQueue(c._key);}}
                      style={{background:"rgba(248,113,113,0.15)", border:"none", color:"#f87171",
                        width:36, height:36, borderRadius:10, fontSize:18, cursor:"pointer", flexShrink:0}}>
                      ✕
                    </button>
                  </div>

                  {isEditing && (
                    <div style={{padding:"0 12px 14px", display:"flex", flexDirection:"column", gap:10}}>
                      <div style={{display:"flex", gap:8}}>
                        <div style={{flex:1}}>
                          <label style={smallLabel}>Market Price ($)</label>
                          <input type="number" min="0" step="0.01" value={c.MarketValue}
                            onChange={e=>updateQueueCard(c._key, {MarketValue:e.target.value})}
                            style={smallInput} placeholder="0.00" />
                        </div>
                        <div style={{flex:1}}>
                          <label style={smallLabel}>List Price ($)</label>
                          <input type="number" min="0" step="0.01" value={c.ListPrice}
                            onChange={e=>updateQueueCard(c._key, {ListPrice:e.target.value})}
                            style={smallInput} placeholder="0.00" />
                        </div>
                      </div>
                      <div style={{display:"flex", gap:8, alignItems:"flex-end"}}>
                        <div style={{flex:1}}>
                          <label style={smallLabel}>Buy %</label>
                          <input type="number" min="0" max="100" value={c.BuyPercentage}
                            onChange={e=>updateQueueCard(c._key, {BuyPercentage:e.target.value === "" ? "" : parseFloat(e.target.value)})}
                            style={smallInput} />
                        </div>
                        <div style={{flex:1, fontSize:13, color:"#88bdf2", paddingBottom:10}}>
                          Acquisition cost: {fmt(acPreview)}
                        </div>
                      </div>
                      <button onClick={()=>setEditingKey(null)}
                        style={{padding:"10px", borderRadius:8, border:"none", background:"#3C66EE",
                          color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer"}}>
                        Done
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showManual && (
          <div style={{marginTop:16, background:T?.surface2||"#1a1f2e", borderRadius:14, padding:16}}>
            <div style={{fontSize:16, fontWeight:700, color:T?.text || "#fff", marginBottom:12}}>
              Add Card Manually
            </div>
            <div style={{display:"flex", gap:8, marginBottom:10}}>
              {GAME_TYPES.map(g=>(
                <button key={g} onClick={()=>setManualForm(f=>({...f,GameType:g}))}
                  style={{flex:1, padding:"10px", borderRadius:10, fontSize:15, fontWeight:600,
                    border:`1px solid ${manualForm.GameType===g?"#3C66EE":(T?.border||"rgba(255,255,255,0.2)")}`,
                    background:manualForm.GameType===g?"rgba(60,102,238,0.15)":"transparent",
                    color:manualForm.GameType===g?"#88bdf2":(T?.textMuted||"#888"), cursor:"pointer"}}>
                  {g}
                </button>
              ))}
            </div>
            <div style={{marginBottom:10}}>
              <input style={{width:"100%", fontSize:16, padding:"12px 14px", borderRadius:10,
                  background:T?.surface||"#0d1117", border:`1px solid ${T?.border||"rgba(255,255,255,0.2)"}`,
                  color:T?.text||"#fff", boxSizing:"border-box"}}
                placeholder="Card name *" value={manualForm.CardName}
                onChange={e=>setManualForm(f=>({...f,CardName:e.target.value}))} />
            </div>
            <div style={{display:"flex", gap:8, marginBottom:14}}>
              <input style={{flex:1, fontSize:16, padding:"12px 14px", borderRadius:10,
                  background:T?.surface||"#0d1117", border:`1px solid ${T?.border||"rgba(255,255,255,0.2)"}`,
                  color:T?.text||"#fff", boxSizing:"border-box"}}
                type="number" min="0" step="0.01" placeholder="Market Price ($)" value={manualForm.MarketValue}
                onChange={e=>setManualForm(f=>({...f,MarketValue:e.target.value}))} />
              <input style={{flex:1, fontSize:16, padding:"12px 14px", borderRadius:10,
                  background:T?.surface||"#0d1117", border:`1px solid ${T?.border||"rgba(255,255,255,0.2)"}`,
                  color:T?.text||"#fff", boxSizing:"border-box"}}
                type="number" min="0" step="0.01" placeholder="List Price ($)" value={manualForm.ListPrice}
                onChange={e=>setManualForm(f=>({...f,ListPrice:e.target.value}))} />
            </div>
            <div style={{display:"flex", gap:8}}>
              <button onClick={()=>setShowManual(false)}
                style={{flex:1, padding:"13px", borderRadius:10, fontSize:15, fontWeight:600,
                  border:`1px solid ${T?.border || "rgba(255,255,255,0.2)"}`, background:"transparent",
                  color:T?.textMuted||"#888", cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={handleManualAdd}
                style={{flex:1, padding:"13px", borderRadius:10, fontSize:15, fontWeight:700,
                  border:"none", background:"#3C66EE", color:"#fff", cursor:"pointer"}}>
                Add to Queue
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{padding:"16px 18px", flexShrink:0, borderTop:`1px solid ${T?.border || "rgba(255,255,255,0.1)"}`,
        display:"flex", flexDirection:"column", gap:10}}>
        {!showManual && (
          <button onClick={()=>setShowManual(true)}
            style={{width:"100%", padding:"14px", borderRadius:12, fontSize:16, fontWeight:600,
              border:`1px solid ${T?.border || "rgba(255,255,255,0.2)"}`, background:"transparent",
              color:T?.textMuted||"#888", cursor:"pointer"}}>
            + Add Manually (non-English cards)
          </button>
        )}
        <div style={{display:"flex", gap:10}}>
          <button onClick={()=>{setPhase("scanning"); setScanning(true);}}
            style={{flex:1, padding:"16px", borderRadius:12, fontSize:17, fontWeight:700,
              border:`1px solid ${T?.border || "rgba(255,255,255,0.2)"}`, background:"transparent",
              color:T?.text||"#fff", cursor:"pointer"}}>
            📷 Scan More
          </button>
          <button onClick={finishAndAdd} disabled={busy || queue.length===0}
            style={{flex:1, padding:"16px", borderRadius:12, fontSize:17, fontWeight:700,
              border:"none", background:"#4ade80", color:"#0d1117", cursor:"pointer",
              opacity:(busy || queue.length===0) ? 0.5 : 1}}>
            {busy ? "Saving…" : "+ to Inventory"}
          </button>
        </div>
      </div>
    </div>
  );
}
